import { useState } from "react";
import { useNavigate } from "react-router";
import type { DuviSharedAdminStatusPillStatus } from "@duvi-agency/ui";
import { users, me, type PlatformUser } from "../../api/client";
import {
  useDuviCoreAdminQuery,
  useDuviCoreAdminMutation,
} from "../../hooks/use-api";

export interface DuviCoreAdminMappedUser {
  id: string;
  name: string;
  email: string;
  avatarUrl?: string;
  status: string;
  mfaEnabled: boolean;
  isSelf: boolean;
}

export function duviCoreAdminMapUserStatus(status: string): string {
  switch (status) {
    case "CONFIRMED":
      return "active";
    case "invited":
      return "invited";
    case "password_reset":
      return "password_reset";
    case "disabled":
    case "DISABLED":
      return "disabled";
    case "archived":
      return "archived";
    default:
      return status.toLowerCase();
  }
}

export function duviCoreAdminUserStatusTone(
  status: string,
): DuviSharedAdminStatusPillStatus {
  switch (status) {
    case "active":
      return "success";
    case "invited":
      return "info";
    case "password_reset":
      return "warning";
    case "disabled":
      return "error";
    case "archived":
      return "inactive";
    default:
      return "info";
  }
}

export function duviCoreAdminUserStatusLabel(status: string): string {
  switch (status) {
    case "active":
      return "Active";
    case "invited":
      return "Invited";
    case "password_reset":
      return "Password Reset";
    case "disabled":
      return "Disabled";
    case "archived":
      return "Archived";
    default:
      return status ? status.charAt(0).toUpperCase() + status.slice(1) : status;
  }
}

export function useDuviCoreAdminUserManagement() {
  const navigate = useNavigate();
  const { data: usersData, refetch } = useDuviCoreAdminQuery(() =>
    users.list(),
  );
  const { data: myProfile } = useDuviCoreAdminQuery(() => me.get(), []);
  const currentUserId = myProfile?.user_id ?? "";

  const inviteMutation = useDuviCoreAdminMutation(
    (input: { email: string; note?: string }) => users.invite(input),
  );
  const disableMutation = useDuviCoreAdminMutation((id: string) =>
    users.disable(id),
  );
  const enableMutation = useDuviCoreAdminMutation((id: string) =>
    users.enable(id),
  );
  const unarchiveMutation = useDuviCoreAdminMutation((id: string) =>
    users.unarchive(id),
  );
  const archiveMutation = useDuviCoreAdminMutation((id: string) =>
    users.archive(id),
  );
  const forceResetMutation = useDuviCoreAdminMutation((id: string) =>
    users.forceResetPassword(id),
  );

  const [showInvitePanel, setShowInvitePanel] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteNote, setInviteNote] = useState("");
  const [inviteSent, setInviteSent] = useState(false);
  const [sentEmail, setSentEmail] = useState("");

  const mappedUsers: DuviCoreAdminMappedUser[] = (usersData ?? []).map(
    (u: PlatformUser) => ({
      id: u.user_id,
      name: u.name,
      email: u.email,
      avatarUrl: u.avatar_url,
      status: duviCoreAdminMapUserStatus(u.status),
      mfaEnabled: u.mfa_enabled ?? false,
      isSelf: u.user_id === currentUserId,
    }),
  );

  const filteredUsers = showArchived
    ? mappedUsers
    : mappedUsers.filter((u) => u.status !== "archived");

  const handleSendInvite = async () => {
    await inviteMutation.mutate({
      email: inviteEmail,
      note: inviteNote || undefined,
    });
    setSentEmail(inviteEmail);
    setInviteSent(true);
    refetch();
  };

  const runAndRefetch = async (fn: () => Promise<unknown>) => {
    await fn();
    refetch();
  };

  const handleDisable = (id: string) =>
    runAndRefetch(() => disableMutation.mutate(id));
  const handleEnable = (id: string) =>
    runAndRefetch(() => enableMutation.mutate(id));
  const handleArchive = (id: string) =>
    runAndRefetch(() => archiveMutation.mutate(id));
  const handleUnarchive = (id: string) =>
    runAndRefetch(() => unarchiveMutation.mutate(id));
  const handleForceReset = (id: string) => {
    void forceResetMutation.mutate(id);
  };

  const openInvite = () => setShowInvitePanel(true);
  const handleInviteAnother = () => {
    setInviteEmail("");
    setInviteNote("");
    setInviteSent(false);
  };
  const handleDone = () => {
    setShowInvitePanel(false);
    setInviteEmail("");
    setInviteNote("");
    setInviteSent(false);
  };

  return {
    filteredUsers,
    showArchived,
    setShowArchived,
    showInvitePanel,
    openInvite,
    inviteEmail,
    setInviteEmail,
    inviteNote,
    setInviteNote,
    inviteSent,
    sentEmail,
    inviteLoading: inviteMutation.isLoading,
    inviteError: inviteMutation.error,
    handleSendInvite,
    handleInviteAnother,
    handleDone,
    handleDisable,
    handleEnable,
    handleArchive,
    handleUnarchive,
    handleForceReset,
    openUser: (id: string) => navigate(`/user-management/${id}`),
  };
}
